import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bot, ArrowRight, Sparkles, Zap, MessageCircle, Menu, X, LogOut, Settings, ChevronDown, Check, CreditCard } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../translations';
import LanguageToggle from './LanguageToggle';
import toast from 'react-hot-toast';

const Home = () => {
  const { language, isRTL } = useLanguage();
  const { user, logout } = useAuth();
  const { t } = useTranslation(language);
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success(t('navigation.logout'));
      navigate('/login');
      setIsProfileMenuOpen(false);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Handle Google OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userData = urlParams.get('user');
    
    if (token && userData) {
      try {
        const user = JSON.parse(decodeURIComponent(userData));
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        window.location.href = '/dashboard';
      } catch (error) {
        console.error('Error processing OAuth callback:', error);
        toast.error('Authentication failed');
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const features = [
    {
      icon: <Bot className="w-5 h-5" />,
      title: t('home.features.smartAI.title'),
      description: t('home.features.smartAI.description')
    },
    {
      icon: <Zap className="w-5 h-5" />,
      title: t('home.features.easySetup.title'),
      description: t('home.features.easySetup.description')
    },
    {
      icon: <MessageCircle className="w-5 h-5" />,
      title: t('home.features.customization.title'),
      description: t('home.features.customization.description')
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Ultra-Glassy Header - Fixed to top */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/50 backdrop-blur-3xl border-b border-white/30 shadow-2xl shadow-gray-300/20" style={{backdropFilter: 'blur(40px) saturate(200%)', WebkitBackdropFilter: 'blur(40px) saturate(200%)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex justify-between items-center h-20">
            
            {/* Elegant Logo */}
            <Link to="/" className={`flex items-center gap-3 group ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="relative">
                <div className="absolute inset-0 bg-blue-600 rounded-xl blur-sm opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                {t('app.name')}
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className={`hidden md:flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <LanguageToggle />
              
              {user ? (
                <>
                  <Link 
                    to="/dashboard" 
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 rounded-lg hover:bg-gray-100/50 transition-all"
                  >
                    {t('navigation.dashboard')}
                  </Link>
                  <Link 
                    to="/create" 
                    className="ml-2 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-all"
                  >
                    {t('navigation.create')}
                  </Link>
                  
                  <div className="relative ml-2" ref={profileMenuRef}>
                    <button
                      onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                      className={`flex items-center gap-2 px-2 py-2 hover:bg-gray-100/50 rounded-lg transition-all ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                      <div className="w-9 h-9 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center text-white text-sm font-semibold ring-2 ring-gray-200">
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isProfileMenuOpen && (
                      <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-3 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 overflow-hidden`}>
                        <div className="px-4 py-4 bg-gradient-to-br from-gray-50 to-white">
                          <p className="text-sm font-semibold text-gray-900">{user.username}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{user.email || 'User Account'}</p>
                        </div>
                        <div className="py-2">
                          <Link
                            to="/wallet"
                            className={`flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <CreditCard className="w-4 h-4" />
                            <span>{t('wallet.title') || 'Wallet & Billing'}</span>
                          </Link>
                          <Link
                            to="/profile"
                            className={`flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <Settings className="w-4 h-4" />
                            <span>{t('navigation.profileSettings')}</span>
                          </Link>
                          <button
                            onClick={handleLogout}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                          >
                            <LogOut className="w-4 h-4" />
                            <span>{t('navigation.logout')}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="ml-2 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-all"
                  >
                    {t('navigation.login')}
                  </Link>
                </>
              )}
            </nav>

            {/* Mobile Menu */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
                <LanguageToggle className="w-full justify-center mb-2" />
                {user ? (
                  <>
                    <div className="px-4 py-4 bg-gray-50 rounded-xl mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center text-white font-semibold">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{user.username}</p>
                          <p className="text-xs text-gray-500">{user.email || 'User Account'}</p>
                        </div>
                      </div>
                    </div>
                    <Link 
                      to="/dashboard" 
                      className="px-4 py-3 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('navigation.dashboard')}
                    </Link>
                    <Link 
                      to="/create" 
                      className="px-4 py-3 bg-gray-900 text-white text-sm font-medium rounded-xl text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('navigation.create')}
                    </Link>
                    <Link 
                      to="/wallet" 
                      className="px-4 py-3 text-center text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('wallet.title') || 'Wallet & Billing'}
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="px-4 py-3 text-center text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      {t('navigation.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    <Link 
                      to="/login" 
                      className="px-4 py-3 bg-gray-900 text-white text-sm font-medium rounded-xl text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('navigation.login')}
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-20"></div>

      {/* Hero Section - Two Column Layout */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-12 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            {/* Left Column - Content */}
            <div className={`${isRTL ? 'lg:order-2' : ''}`}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full mb-4 sm:mb-6 shadow-sm">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-gray-700 tracking-wide uppercase">{t('app.tagline')}</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-4 sm:mb-6 leading-[1.1] tracking-tight" style={isRTL ? {textAlign: 'justify'} : {}}>
                {t('home.hero.title')}
                <br />
                <span className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 bg-clip-text text-transparent">
                  {t('home.hero.titleHighlight')}
                </span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed max-w-xl" style={isRTL ? {textAlign: 'justify'} : {}}>
                {t('home.hero.subtitle')}
              </p>
              
              <div className={`flex flex-col sm:flex-row gap-3 sm:gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                <Link 
                  to="/create" 
                  className={`group inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3 sm:py-4 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl text-sm sm:text-base ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <span>{t('home.hero.getStarted')}</span>
                  <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 group-hover:${isRTL ? '-translate-x-1' : 'translate-x-1'} transition-transform`} />
                </Link>
                <Link 
                  to="/dashboard" 
                  className="inline-flex items-center justify-center px-6 sm:px-7 py-3 sm:py-4 bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-xl border border-gray-200 transition-all text-sm sm:text-base"
                >
                  {t('home.hero.viewDemo')}
                </Link>
              </div>
            </div>

            {/* Right Column - Visual Element */}
            <div className={`relative ${isRTL ? 'lg:order-1' : ''}`}>
              <div className="relative bg-gradient-to-br from-blue-50 to-gray-100 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 border border-gray-200">
                <div className="absolute top-4 sm:top-6 right-4 sm:right-6 w-24 h-24 sm:w-32 sm:h-32 bg-blue-200/30 rounded-full blur-3xl"></div>
                <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 w-24 h-24 sm:w-32 sm:h-32 bg-purple-200/30 rounded-full blur-3xl"></div>
                
                <div className="relative space-y-3 sm:space-y-4">
                  {/* Mock Chat Bubbles */}
                  <div className="flex gap-2 sm:gap-3 items-start">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <div className="bg-white rounded-xl sm:rounded-2xl rounded-tl-sm px-3 sm:px-5 py-2 sm:py-3 shadow-sm border border-gray-200 max-w-xs">
                      <p className="text-xs sm:text-sm text-gray-900">Hello! How can I help you today?</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 sm:gap-3 items-start justify-end">
                    <div className="bg-gray-900 rounded-xl sm:rounded-2xl rounded-tr-sm px-3 sm:px-5 py-2 sm:py-3 shadow-sm max-w-xs">
                      <p className="text-xs sm:text-sm text-white">I need help with my order</p>
                    </div>
                  </div>

                  <div className="flex gap-2 sm:gap-3 items-start">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <div className="bg-white rounded-xl sm:rounded-2xl rounded-tl-sm px-3 sm:px-5 py-2 sm:py-3 shadow-sm border border-gray-200 max-w-xs">
                      <p className="text-xs sm:text-sm text-gray-900">I'd be happy to assist! What's your order number?</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('home.features.title')}
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              {t('home.features.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-gray-50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative h-full bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 hover:border-gray-300 transition-all">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gray-900 rounded-xl flex items-center justify-center text-white mb-4 sm:mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-12 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
              {t('home.howItWorks.title')}
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              {t('home.howItWorks.subtitle')}
            </p>
          </div>

          <div className="space-y-6 sm:space-y-8">
            {/* Step 1 */}
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 bg-white rounded-2xl p-6 sm:p-8 border border-gray-200">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gray-900 rounded-xl flex items-center justify-center text-white font-bold text-sm sm:text-base">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  {t('home.howItWorks.step1.title')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {t('home.howItWorks.step1.description')}
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 bg-white rounded-2xl p-6 sm:p-8 border border-gray-200">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gray-900 rounded-xl flex items-center justify-center text-white font-bold text-sm sm:text-base">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  {t('home.howItWorks.step2.title')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {t('home.howItWorks.step2.description')}
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 bg-white rounded-2xl p-6 sm:p-8 border border-gray-200">
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gray-900 rounded-xl flex items-center justify-center text-white font-bold text-sm sm:text-base">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                  {t('home.howItWorks.step3.title')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {t('home.howItWorks.step3.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]"></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed">
            {t('home.cta.subtitle')}
          </p>
          <Link 
            to="/create" 
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white hover:bg-gray-100 text-gray-900 font-bold rounded-xl transition-all shadow-2xl text-sm sm:text-base"
          >
            <span>{t('home.cta.button')}</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-4 px-4 sm:px-6 lg:px-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-6 h-6 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <Bot className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm font-bold text-gray-900">{t('app.name')}</span>
            </div>
            <p className="text-xs text-gray-600">
              © 2025 {t('app.name')} - MSF Company. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
